import { useEffect } from "react";

const SITE_NAME = "Listaa";
const SITE_URL = "https://listaa.com";
const DEFAULT_DESCRIPTION =
  "Listaa is the marketplace and community for discovering, booking, and listing the best venues worldwide — nightclubs, restaurants, lounges, bars, and more.";
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

export interface SEOProps {
  title: string;
  description?: string;
  image?: string;
  url?: string;
  noIndex?: boolean;
  type?: "website" | "article";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  structuredData?: Record<string, any> | Record<string, any>[];
}

function setMeta(name: string, content: string, attr: "name" | "property" = "name") {
  let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel: string, href: string) {
  let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function setJsonLd(id: string, data: Record<string, any> | Record<string, any>[]) {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removeJsonLd(id: string) {
  document.getElementById(id)?.remove();
}

export function useSEO({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  url,
  noIndex = false,
  type = "website",
  structuredData,
}: SEOProps) {
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const canonical =
    url ?? (typeof window !== "undefined" ? `${SITE_URL}${window.location.pathname}` : SITE_URL);

  useEffect(() => {
    document.title = fullTitle;

    // Standard
    setMeta("description", description);
    setMeta("robots", noIndex ? "noindex, nofollow" : "index, follow");
    setMeta("googlebot", noIndex ? "noindex, nofollow" : "index, follow");

    // Open Graph
    setMeta("og:type", type, "property");
    setMeta("og:site_name", SITE_NAME, "property");
    setMeta("og:title", fullTitle, "property");
    setMeta("og:description", description, "property");
    setMeta("og:image", image, "property");
    setMeta("og:url", canonical, "property");

    // Twitter Card
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", description);
    setMeta("twitter:image", image);

    // Canonical
    setLink("canonical", canonical);

    // JSON-LD
    if (structuredData) {
      setJsonLd("structured-data", structuredData);
    } else {
      removeJsonLd("structured-data");
    }
  }, [fullTitle, description, image, canonical, noIndex, type, structuredData]);
}

// ── Structured data builders ──────────────────────────────────────────────────

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Listaa",
  url: SITE_URL,
  logo: `${SITE_URL}/web-app-manifest-512x512.png`,
  sameAs: [
    "https://instagram.com/listaanyc",
    "https://www.facebook.com/profile.php?id=61595296167539",
    // "https://twitter.com/listaa",
  ],
  description: DEFAULT_DESCRIPTION,
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Listaa",
  url: SITE_URL,
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${SITE_URL}/search?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
};

export function venueSchema(venue: {
  name: string;
  description?: string | null;
  address?: string | null;
  city?: string | null;
  country?: string | null;
  phone?: string | null;
  website?: string | null;
  image?: string | null;
  slug?: string | null;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: venue.name,
    description: venue.description ?? undefined,
    address: venue.address
      ? {
          "@type": "PostalAddress",
          streetAddress: venue.address,
          addressLocality: venue.city ?? undefined,
          addressCountry: venue.country ?? undefined,
        }
      : undefined,
    telephone: venue.phone ?? undefined,
    url: venue.slug ? `${SITE_URL}/${venue.slug}` : SITE_URL,
    image: venue.image ?? undefined,
    sameAs: venue.website ? [venue.website] : undefined,
  };
}
